export const REFERRAL_STATUSES = [
  "active",
  "screening",
  "approved-pending",
  "moved-in",
  "approved-no-move",
  "waitlist",
  "inactive",
  "denied",
] as const;

export type ReferralStatus = (typeof REFERRAL_STATUSES)[number];
export type RecurrenceType = "daily" | "weekly" | "monthly" | "custom";
export const SOURCE_REFERRAL_PRIORITIES = ["daily", "weekly", "monthly", "other"] as const;
export type SourceReferralPriority = (typeof SOURCE_REFERRAL_PRIORITIES)[number];

const STATUS_ALIASES: Record<string, ReferralStatus> = {
  active: "active",
  new: "active",
  inquiry: "active",
  prospect: "active",
  screening: "screening",
  assessment: "screening",
  "assessment scheduled": "screening",
  approved: "approved-pending",
  "approved pending": "approved-pending",
  "approved - pending": "approved-pending",
  "pending move in": "approved-pending",
  "moved in": "moved-in",
  admitted: "moved-in",
  resident: "moved-in",
  "approved no move": "approved-no-move",
  "approved not moved": "approved-no-move",
  waitlist: "waitlist",
  "waiting list": "waitlist",
  inactive: "inactive",
  closed: "inactive",
  denied: "denied",
  declined: "denied",
  "not approved": "denied",
};

export function normalizeText(value: unknown): string {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export function mapLegacySourceActivityType(value: unknown): string {
  const normalized = normalizeText(value);
  if (normalized === "phone") return "call";
  if (["email", "event", "marketing", "meeting", "visit"].includes(normalized)) return normalized;
  return "legacy-outreach";
}

export function mapLegacySourcePriority(value: unknown): {
  priority: SourceReferralPriority;
  cadenceType: RecurrenceType;
  cadenceDays: number | null;
  recognized: boolean;
} {
  const normalized = normalizeText(value);
  if (normalized === "daily") return { priority: "daily", cadenceType: "daily", cadenceDays: 1, recognized: true };
  if (["weekly", "wekly"].includes(normalized)) return { priority: "weekly", cadenceType: "weekly", cadenceDays: 7, recognized: true };
  if (normalized === "monthly") return { priority: "monthly", cadenceType: "monthly", cadenceDays: null, recognized: true };
  if (["other", ""].includes(normalized)) return { priority: "other", cadenceType: "custom", cadenceDays: null, recognized: true };
  const days = Number(normalized.match(/\d+/)?.[0]);
  return {
    priority: "other",
    cadenceType: "custom",
    cadenceDays: Number.isFinite(days) && days > 0 ? days : null,
    recognized: Number.isFinite(days) && days > 0,
  };
}

export function mapLegacyStatus(value: unknown): {
  code: ReferralStatus;
  original: string;
  recognized: boolean;
} {
  const original = String(value ?? "").trim();
  const normalized = normalizeText(original);
  let code = STATUS_ALIASES[normalized];
  if (!code) {
    if (normalized.startsWith("active")) code = "active";
    else if (normalized.startsWith("screening")) code = "screening";
    else if (normalized.startsWith("approved moved in")) code = "moved-in";
    else if (normalized.startsWith("approved move in pending")) code = "approved-pending";
    else if (normalized.startsWith("approved did not move")) code = "approved-no-move";
    else if (normalized.startsWith("approved on wait")) code = "waitlist";
    else if (normalized.startsWith("inactive")) code = "inactive";
    else if (normalized.startsWith("denied")) code = "denied";
  }
  return {
    code: code ?? "active",
    original,
    recognized: Boolean(code),
  };
}

export function isOverdue(dueAt: string | Date, now = new Date()): boolean {
  const due = typeof dueAt === "string" ? new Date(dueAt) : dueAt;
  return !Number.isNaN(due.getTime()) && due.getTime() < now.getTime();
}

export function calendarDaysSince(value: string | Date | null | undefined, now = new Date()): number | null {
  if (!value) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime()) || Number.isNaN(now.getTime())) return null;
  const dateDay = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const currentDay = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((currentDay - dateDay) / 86_400_000));
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

export function nextRecurrenceDate(
  completedAt: string | Date,
  type: RecurrenceType,
  customDays?: number,
): Date {
  const completed = typeof completedAt === "string" ? new Date(completedAt) : completedAt;
  if (Number.isNaN(completed.getTime())) throw new Error("Invalid completion date");

  const next = new Date(completed);
  if (type === "daily") next.setUTCDate(next.getUTCDate() + 1);
  if (type === "weekly") next.setUTCDate(next.getUTCDate() + 7);
  if (type === "custom") {
    if (!customDays || customDays < 1) throw new Error("Custom cadence requires recurrence days");
    next.setUTCDate(next.getUTCDate() + customDays);
  }
  if (type === "monthly") {
    const day = next.getUTCDate();
    const targetMonth = next.getUTCMonth() + 1;
    const targetYear = next.getUTCFullYear() + Math.floor(targetMonth / 12);
    const month = targetMonth % 12;
    next.setUTCFullYear(targetYear, month, Math.min(day, daysInMonth(targetYear, month)));
  }
  return next;
}

export function excelSerialToDate(serial: number, date1904 = false): Date | null {
  if (!Number.isFinite(serial) || serial <= 0) return null;
  const epoch = Date.UTC(date1904 ? 1904 : 1899, date1904 ? 0 : 11, date1904 ? 1 : 30);
  const wholeDays = Math.floor(serial);
  const fraction = serial - wholeDays;
  const result = new Date(epoch + wholeDays * 86_400_000 + Math.round(fraction * 86_400_000));
  return Number.isNaN(result.getTime()) ? null : result;
}

export function zonedDateParts(date: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
    weekday: "short",
  }).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return {
    year: Number(read("year")),
    month: Number(read("month")),
    day: Number(read("day")),
    hour: Number(read("hour")),
    weekday: read("weekday"),
    localDate: `${read("year")}-${read("month")}-${read("day")}`,
  };
}

export function shouldSendDigest(
  now: Date,
  timezone: string,
  digestHour: number,
  alreadyDelivered: boolean,
): boolean {
  if (alreadyDelivered) return false;
  const parts = zonedDateParts(now, timezone);
  return !["Sat", "Sun"].includes(parts.weekday) && parts.hour === digestHour;
}

export function addBusinessDays(date: Date, days: number): Date {
  const result = new Date(date);
  let remaining = days;
  while (remaining > 0) {
    result.setUTCDate(result.getUTCDate() + 1);
    const day = result.getUTCDay();
    if (day !== 0 && day !== 6) remaining -= 1;
  }
  return result;
}
